import React, { FC } from "react";
import { Box, FormControl, Text, TextInput } from "@primer/react";
import { Banner } from "@primer/react/experimental";
import { InvitationStatusBreakdown } from "./InvitationStatusContent";

interface InvitationDeleteContentProps {
  selectionCount: number;
  preflightCounts?: Record<string, number>;
  confirmValue: string;
  onConfirmValueChange: (value: string) => void;
  requiredConfirmText: string;
}

// Unlike RegistrationDeleteExecutor (cancelled-only), InvitationDeleteExecutor
// deletes every selected invitation regardless of status — matching the
// single-row delete. So there is no "skipped" copy; the breakdown is purely
// informational.
const describeDeleteRow = () => "Will be permanently deleted";

export const InvitationDeleteContent: FC<InvitationDeleteContentProps> = ({
  selectionCount,
  preflightCounts,
  confirmValue,
  onConfirmValueChange,
  requiredConfirmText,
}) => {
  const invitationLabel = selectionCount === 1 ? "invitation" : "invitations";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Text as="div" fontSize={1}>
        Deleting{" "}
        <Text as="span" fontWeight="bold">{selectionCount} {invitationLabel}</Text>
      </Text>

      <Banner
        variant="critical"
        title={`You are about to permanently delete ${selectionCount} ${invitationLabel}. This action cannot be undone.`}
      />

      {preflightCounts && (
        <InvitationStatusBreakdown counts={preflightCounts} describeRow={describeDeleteRow} />
      )}

      <FormControl>
        <FormControl.Label>To confirm, type &lsquo;{requiredConfirmText}&rsquo; below</FormControl.Label>
        <TextInput
          block
          value={confirmValue}
          onChange={(e) => onConfirmValueChange(e.target.value)}
          aria-label={`Type ${requiredConfirmText} to confirm`}
        />
      </FormControl>

      <Text as="div" fontSize={1}>
        Are you sure you want to permanently delete{" "}
        <Text as="span" fontWeight="bold">{selectionCount} {invitationLabel}</Text>?
      </Text>
    </Box>
  );
};
